import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useProject } from '../../store/projectContext';
import { SceneRenderer } from './SceneRenderer';
import { AudioSequences } from './AudioSequences';
import { Sequence } from '../Sequence';
import { FireWorks } from '../fireworks/FireWorks';
import SlideTransitions from '../Transitions/SlideTransitions';
import { useActiveSceneWindow } from '../../hooks/useActiveSceneWindow';
import { useProgressivePrefetch } from '../../hooks/useProgressivePrefetch';
import { useTimelinePosition } from '../timeline-position-state';
 
const PlayerContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background: #000;
`;

const OverlayLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 5;
`;

export function ScenePlayer() {
  const project = useProject();
  const fps = project?.fps || 30;
  const { isSceneNearby } = useActiveSceneWindow(3);
  const frame = useTimelinePosition();

  // Non-blocking background prefetch for upcoming scenes
  useProgressivePrefetch();

  const sceneSequences = useMemo(() => {
    if (!project?.scenes) return [];
    let cumulativeFrame = 0;
    return project.scenes.map((scene) => {
      const from = cumulativeFrame;
      const durationInFrames = Math.round((scene.duration || 4) * fps);
      cumulativeFrame += durationInFrames;
      return { scene, from, durationInFrames };
    });
  }, [project?.scenes, fps]);

  if (!project?.scenes?.length) {
    return <PlayerContainer />;
  }

  const totalFrames = sceneSequences.reduce((sum, s) => sum + s.durationInFrames, 0);

  const hasTransitions = project.scenes.some(
    (s) => s.transition && s.transition.effect && s.transition.effect !== 'none'
  );

  return (
    <PlayerContainer>
      {hasTransitions ? (
        (() => {
          let cumulatedDuration = 0;
          return project.scenes.map((scene, index) => {
            const startFrame = Math.round(cumulatedDuration * fps);
            const durationInFrames = Math.round((scene.duration || 4) * fps);
            cumulatedDuration += scene.duration || 4;
            const isActive = frame >= startFrame && frame < startFrame + durationInFrames;
            return (
              <div
                key={`${scene.id || 'scene'}-${index}`}
                style={{ position: 'absolute', inset: 0, zIndex: index }}
              >
                <SlideTransitions
                  slide={index}
                  cumulatedDuration={cumulatedDuration}
                >
                  <SceneRenderer
                    scene={scene}
                    projectWidth={project.width}
                    projectHeight={project.height}
                    isNearby={isSceneNearby(index)}
                    isActive={isActive}
                  />
                </SlideTransitions>
              </div>
            );
          });
        })()
      ) : (
        sceneSequences.map(({ scene, from, durationInFrames }, index) => {
          const isActive = frame >= from && frame < from + durationInFrames;
          return (
            <Sequence
              key={`${scene.id || 'scene'}-${index}`}
              from={from}
              durationInFrames={durationInFrames}
              name={`Scene ${index + 1}`}
              premountFor={90}
            >
              <SceneRenderer
                scene={scene}
                projectWidth={project.width}
                projectHeight={project.height}
                isNearby={isSceneNearby(index)}
                isActive={isActive}
              />
            </Sequence>
          );
        })
      )}

      {/* Component overlay (e.g. fireworks animation) */}
      {project.componentOverlay === 'fireworks' && (
        <OverlayLayer>
          <Sequence from={0} durationInFrames={totalFrames} layout="none">
            <FireWorks />
          </Sequence>
        </OverlayLayer>
      )}

      {/* Audio tracks (TTS + music) — rendered as Remotion Audio sequences */}
      <AudioSequences />
    </PlayerContainer>
  );
}
